import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usePregnancies } from '@/features/emr';
import { P, usePermissions } from '@/features/permissions';
import { useCreatePatient, usePatients } from '@/features/emr';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { apiError, type Study, useImagingApi, useImagingQuery } from './api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function StudySetup({ study, onSaved }: { study?: Study; onSaved?: (study: Study) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" onClick={() => setOpen(true)}>
        {study ? 'Schedule / reschedule study' : 'Create / schedule study'}
      </Button>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{study ? 'Schedule study' : 'Create imaging study'}</DialogTitle>
          <DialogDescription>
            Choose the patient and appointment time. Images can be added before or after the appointment.
          </DialogDescription>
        </DialogHeader>
        {open && (
          <StudyForm
            study={study}
            saved={(result) => {
              onSaved?.(result);
              setOpen(false);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function StudyForm({ study, saved }: { study?: Study; saved: (study: Study) => void }) {
  const { org, base } = useImagingApi();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const settings = useImagingQuery<{ time_zone: string | null }>('/schedule-settings');
  const [search, setSearch] = useState('');
  const [patient, setPatient] = useState(study?.patient_id || '');
  const [newPatient, setNewPatient] = useState(false);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [mrn, setMrn] = useState('');
  const [pregnancy, setPregnancy] = useState(study?.pregnancy_id || '');
  const [description, setDescription] = useState(study?.description || 'Ultrasound');
  const [when, setWhen] = useState(() => {
    if (!study?.scheduled_at) return '';
    const parts = new Intl.DateTimeFormat('sv-SE', {
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      month: '2-digit',
      hourCycle: 'h23',
      minute: '2-digit',
      timeZone: study.scheduled_timezone || 'UTC',
    }).format(study.scheduled_at ? new Date(study.scheduled_at) : undefined);
    return parts.replace(' ', 'T');
  });
  const initialized = useRef(false);
  useEffect(() => {
    if (!settings.data || initialized.current) return;
    initialized.current = true;
    if (study?.scheduled_at && settings.data.time_zone) {
      const value = new Intl.DateTimeFormat('sv-SE', {
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        month: '2-digit',
        hourCycle: 'h23',
        minute: '2-digit',
        timeZone: settings.data.time_zone,
      }).format(new Date(study.scheduled_at));
      setWhen(value.replace(' ', 'T'));
    }
  }, [settings.data, study]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const key = useRef(crypto.randomUUID());
  const patients = usePatients({ search, limit: 30 });
  const pregnancies = usePregnancies(patient || undefined);
  const createPatient = useCreatePatient();
  async function submit() {
    setBusy(true);
    setError('');
    try {
      let id = patient;
      if (!study && newPatient && !id) {
        const created = await createPatient.mutateAsync({
          medical_record_number: mrn.trim(),
          metadata_: { date_of_birth: dob, last_name: last.trim(), first_name: first.trim() },
        });
        id = created.id;
        setPatient(id);
        setNewPatient(false);
      }
      const { data } = study
        ? await clientApi.put<Study>(`${base}/studies/${study.id}/schedule`, {
            revision: study.revision,
            scheduled_local: when || null,
          })
        : await clientApi.post<Study>(`${base}/studies`, {
            patient_id: id,
            idempotency_key: key.current,
            scheduled_local: when || null,
            pregnancy_id: pregnancy || null,
            description: description.trim(),
          });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['imaging', org] }),
        qc.invalidateQueries({ queryKey: ['calendar', org] }),
      ]);
      toast.success(when ? 'Study saved on the calendar.' : 'Study created without an appointment.');
      saved(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}>
      <FieldGroup>
        {!study && (
          <>
            {can(P.CREATE_PATIENT) && (
              <Button
                type="button"
                disabled={busy}
                variant="outline"
                onClick={() => {
                  setNewPatient(!newPatient);
                  setPatient('');
                  setPregnancy('');
                }}>
                {newPatient ? 'Choose existing patient' : 'Register new patient'}
              </Button>
            )}
            {newPatient ? (
              <>
                <Field>
                  <FieldLabel htmlFor="study-first">First name</FieldLabel>
                  <Input id="study-first" required value={first} onChange={(e) => setFirst(e.target.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="study-last">Last name</FieldLabel>
                  <Input id="study-last" required value={last} onChange={(e) => setLast(e.target.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="study-dob">Date of birth</FieldLabel>
                  <Input id="study-dob" required type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
                </Field>
                <Field>
                  <FieldLabel htmlFor="study-mrn">Medical record number</FieldLabel>
                  <Input id="study-mrn" required value={mrn} onChange={(e) => setMrn(e.target.value)} />
                </Field>
              </>
            ) : (
              <Field>
                <FieldLabel htmlFor="study-patient-search">Patient</FieldLabel>
                <Input
                  id="study-patient-search"
                  value={search}
                  placeholder="Search patients"
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Select
                  value={patient}
                  onValueChange={(v) => {
                    setPatient(v);
                    setPregnancy('');
                  }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {patients.data?.data.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.metadata_?.first_name} {p.metadata_?.last_name} · {p.medical_record_number} ·{' '}
                          {p.metadata_?.date_of_birth}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {patients.isError && <p role="alert">{apiError(patients.error)}</p>}
                {patient && !patients.data?.data.some((p) => p.id === patient) && (
                  <p>Patient selected. You may continue.</p>
                )}
              </Field>
            )}
            {patient && (
              <Field>
                <FieldLabel>Pregnancy (optional for scheduling)</FieldLabel>
                <Select value={pregnancy || 'none'} onValueChange={(v) => setPregnancy(v === 'none' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="none">Assign before preparing the report</SelectItem>
                      {pregnancies.data?.data.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.status} · EDD {p.edd || 'unknown'}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field>
              <FieldLabel htmlFor="study-description">Study description</FieldLabel>
              <Input
                id="study-description"
                required
                maxLength={256}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </>
        )}
        <Field>
          <FieldLabel htmlFor="study-time">
            Appointment date and time ({settings.data?.time_zone || 'practice timezone not configured'})
          </FieldLabel>
          <Input
            id="study-time"
            value={when}
            type="datetime-local"
            disabled={!settings.data}
            onChange={(e) => setWhen(e.target.value)}
          />
          <p>Leave blank for an unscheduled study. A scheduled study appears on the calendar.</p>
        </Field>
      </FieldGroup>
      {settings.isError && <p role="alert">{apiError(settings.error)}</p>}
      {error && <p role="alert">{error}</p>}
      <Button
        type="submit"
        disabled={busy || (!study && !patient && !newPatient) || (!!when && !settings.data?.time_zone)}>
        {busy ? 'Saving…' : 'Save study'}
      </Button>
    </form>
  );
}
